import { apiGet, apiPost } from '@forwardflow/services/Axios/configs'
import CampaignService from '@forwardflow/services/CampaignService'
import { FubService } from '@forwardflow/services/fub.service'
import { OnboardingV2Service } from '@forwardflow/services/onboardingV2.service'
import { useAppStore } from '@forwardflow/store/appStore'
import { ICampaignV2Api } from '@forwardflow/features/campaign-v2/services/campaign-v2.api'
import { liveCampaignV2Api } from '@forwardflow/features/campaign-v2/services/campaign-v2.live'
import { idempotencyKey } from '@forwardflow/features/campaign-v2/utils/config'
import {
   CampaignV2Error,
   IAvailableNumber,
   ICadenceStep,
   ICatalogItem,
   IConfigurationRead,
   IContentReference,
   IContentRevision,
   IConversationDefinition,
   IPhoneDefinition,
   IStrategyDefinition,
   TContentKind,
} from '@forwardflow/features/campaign-v2/interfaces/campaign-v2.types'
import {
   NUMBER_PRICE_PER_MONTH,
   ORG,
   RECOMMENDED_BY_TYPE,
   RECOMMENDED_PLAYBOOK,
   SAMPLE_LEAD,
} from '@forwardflow/features/campaign-flow/data/mock'
import { UserService } from '@forwardflow/services/UserService'
import {
   IActivityItem,
   IAudience,
   ICampaignStats,
   IContentOption,
   ICampaignRow,
   IFlowDraft,
   IMetric,
   IOverviewStats,
   IPhoneNumberOption,
   IPlaybook,
   IResults,
   IVoiceOption,
   TActivityKind,
   TCampaignKind,
   TLeadType,
   TPeriod,
   TPlaybookContent,
} from '@forwardflow/features/campaign-flow/interfaces/campaign-flow.types'
import { ICampaignFlowService } from '@forwardflow/features/campaign-flow/services/campaign-flow.service'
import {
   LEAD_TYPE_LABEL,
   TFilterVocabulary,
   audienceTypes,
   interpretDescription,
} from '@forwardflow/features/campaign-flow/utils/describe'
import {
   pickContent,
   viewFor,
   withTypes,
} from '@forwardflow/features/campaign-flow/utils/playbook'
import {
   PICK,
   TDayRow,
   buckets,
   countQueries,
   kindCriteria,
   lastActive,
   localHour,
   num,
   periodWindows,
   ratioMetric,
   timeFor,
   windowMetric,
} from '@forwardflow/features/campaign-flow/utils/live-derive'
import {
   clockTime,
   fromTemplate,
   plural,
} from '@forwardflow/features/campaign-flow/utils/text'
import {
   IContentSnapshot,
   playbookFromContent,
} from '@forwardflow/features/campaign-flow/utils/from-content'
import {
   localReadiness,
   reasonToItem,
} from '@forwardflow/features/campaign-flow/utils/readiness'
import {
   IPlaybookRefs,
   TBusinessTypeIds,
   TPlaybookRefsByType,
   toConfiguration,
   toE164,
   toPhoneDefinition,
   toStrategyDefinition,
} from '@forwardflow/features/campaign-flow/utils/to-configuration'

/**
 * The guided flow on the real backend (campaign v2 handoff; plan in the
 * "Campaign flow · API integration plan" doc).
 *
 * Every write goes through the existing v2 adapter (`campaign-v2.live.ts`),
 * so URLs, envelopes and errors are handled in one place. This file only
 * decides the order of calls and remembers what the backend handed back.
 */

/* ---------- What one flow session remembers ---------- */

type TContentSlot = 'phone' | 'strategy' | 'conversation'

interface IBoundContent {
   categoryId: number
   /** Per slot: a fingerprint of the words, to know when the words changed. */
   hashes: Record<TContentSlot, string>
   refs: IPlaybookRefs
}

export interface ILiveSession {
   campaignId?: string
   revision?: number
   /** One key per deliberate create, reused if the first try never answered. */
   createKey?: string
   activateKey?: string
   /** Library items behind the recommended words, per business category id. */
   recommendedBy?: Record<string, IBoundContent>
   /** Library items holding this campaign's words, per business category id. */
   boundBy?: Record<string, IBoundContent>
   assistant?: { id: string | number; voiceId: string; name: string }
   /** Numbers reserved for this campaign (E.164). Bought at launch. */
   reserved?: string[]
}

const SESSION_KEY = 'ff-campaign-flow-live'

const readSession = (): ILiveSession => {
   try {
      return JSON.parse(
         sessionStorage.getItem(SESSION_KEY) || '{}'
      ) as ILiveSession
   } catch {
      return {}
   }
}
const writeSession = (s: ILiveSession) => {
   try {
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(s))
   } catch {
      /* private mode: this tab still works from memory */
   }
}
let session: ILiveSession = readSession()
const remember = (patch: Partial<ILiveSession>) => {
   session = { ...session, ...patch }
   writeSession(session)
}

/** "New campaign" starts a fresh session; nothing from the last one is reused. */
export const startNewLiveSession = () => {
   session = {}
   writeSession(session)
}

/** For tests. */
export const liveSessionNow = () => session

/* ---------- Small helpers ---------- */

let api: ICampaignV2Api = liveCampaignV2Api
/** Tests swap in a fake adapter. */
export const setApiForTests = (a: ICampaignV2Api) => {
   api = a
   categoryCache = null
   voiceCache = null
   recommendedCache.clear()
   seen.clear()
}

const orgId = () => useAppStore.getState().currentOrganization || ''

/** Stable orb hue from a voice id, so a voice keeps its colour everywhere. */
export const hueOf = (id: string) => {
   let h = 0
   for (const ch of id) h = (h * 31 + ch.charCodeAt(0)) % 360
   return h
}

/** "+17195550140" → "(719) 555-0140" */
export const toDisplay = (e164: string) => {
   const d = e164.replace(/\D/g, '').replace(/^1(?=\d{10}$)/, '')
   return d.length === 10
      ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`
      : e164
}
const areaOf = (e164: string) =>
   e164
      .replace(/\D/g, '')
      .replace(/^1(?=\d{10}$)/, '')
      .slice(0, 3)

const listOf = (v: unknown): Record<string, unknown>[] => {
   if (Array.isArray(v)) return v as Record<string, unknown>[]
   const inner = (v as { data?: unknown } | null)?.data
   return Array.isArray(inner) ? (inner as Record<string, unknown>[]) : []
}

/** This endpoint represents an empty page as a specific 404, not an empty array. */
const noCampaigns = (error: unknown) => {
   const e = error as { response?: { status?: number; data?: { message?: string } } }
   return e?.response?.status === 404 && e.response.data?.message === 'No active campaigns'
}

const allCampaignRows = async () => {
   const result: Record<string, unknown>[] = []
   const ids = new Set<string>()
   for (let page = 1; ; page++) {
      let batch: Record<string, unknown>[]
      try {
         batch = listOf(await CampaignService.getActiveCampaigns({
            page, limit: 50, status: 'active,paused,draft,inactive',
         }))
      } catch (error) {
         if (noCampaigns(error)) return result
         throw error
      }
      const fresh = batch.filter((row) => !ids.has(String(row.id)))
      if (batch.length && !fresh.length) throw new Error('Could not load the next page of campaigns. Please retry.')
      fresh.forEach((row) => { ids.add(String(row.id)); result.push(row) })
      if (batch.length < 50) return result
   }
}

const ago = (iso: unknown) => {
   const t = Date.parse(String(iso || ''))
   if (Number.isNaN(t)) return '—'
   const mins = Math.round((Date.now() - t) / 60000)
   if (mins < 60) return `${Math.max(mins, 1)} min ago`
   const hrs = Math.round(mins / 60)
   if (hrs < 24) return `${hrs} hr ago`
   return `${Math.round(hrs / 24)} days ago`
}

/* ---------- Business categories (lead types) ---------- */

const TYPE_MATCH: Record<TLeadType, RegExp> = {
   buyer: /buy/i,
   seller: /sell|listing/i,
   rental: /rent|tenant|landlord|lease/i,
}
let categoryCache: TBusinessTypeIds | null = null
const recommendedCache = new Map<
   number,
   { playbook: IPlaybook; bound: IBoundContent }
>()

export const categoryIds = async (): Promise<TBusinessTypeIds> => {
   if (categoryCache) return categoryCache
   const list = await api.businessCategories()
   const pick = (t: TLeadType) =>
      list.find((c) => TYPE_MATCH[t].test(`${c.slug || ''} ${c.name}`))?.id
   const ids = {
      buyer: pick('buyer'),
      seller: pick('seller'),
      rental: pick('rental'),
   }
   categoryCache = ids as TBusinessTypeIds
   return categoryCache
}

const categoryFor = (ids: TBusinessTypeIds, types: TLeadType[]) => {
   const missing = types.filter((t) => !ids[t])
   if (missing.length)
      throw new Error(
         `Your account has no category for ${missing.join(' and ')} leads yet. Ask support to add it, or untick it.`
      )
   return ids[types[0]]
}

/* ---------- Content ---------- */

const hashes = (pb: IPlaybook): Record<TContentSlot, string> => ({
   phone: JSON.stringify(pb.script.map((s) => [s.title, s.text])),
   strategy: JSON.stringify([
      pb.texts.map((t) => t.text),
      pb.voicemails.map((v) => v.text),
   ]),
   conversation: pb.replyInstructions,
})

const refOf = (rev: IContentRevision): IContentReference => ({
   id: String(rev.contentId),
   revisionId: rev.id,
})

/** An outbound call script the assistant can use. */
const isOutboundAi = (p: ICatalogItem) =>
   (p.callDirections || ['outbound']).includes('outbound') &&
   (!p.callerTypes || p.callerTypes.includes('ai'))
const pickOutbound = (items: ICatalogItem[]) => items.find(isOutboundAi)
const pickInbound = (items: ICatalogItem[]) =>
   items.find(
      (p) =>
         (p.callDirections || []).includes('inbound') &&
         (!p.leadTypes || p.leadTypes.includes('known'))
   )

/**
 * The words the screens show come from the library's recommended items for
 * the first lead type. The references are kept so an unchanged playbook is
 * used by reference and nothing new is written to the library.
 */
const loadRecommended = async (
   categoryId: number
): Promise<{ playbook: IPlaybook; bound: IBoundContent } | null> => {
   const [phones, strategies, conversations] = await Promise.all([
      api.catalog('phone', categoryId),
      api.catalog('strategy', categoryId),
      api.catalog('conversation', categoryId),
   ])
   const outbound = pickOutbound(phones)
   const inbound = pickInbound(phones)
   const strategy = strategies[0]
   const conversation = conversations[0]
   if (!outbound || !strategy || !conversation) return null
   const acquire = (kind: TContentKind, item: ICatalogItem) =>
      api.acquireRevision(kind, item.contentId)
   const [p, s, c, i] = await Promise.all([
      acquire('phone', outbound),
      acquire('strategy', strategy),
      acquire('conversation', conversation),
      inbound ? acquire('phone', inbound) : Promise.resolve(null),
   ])
   const snapshot: IContentSnapshot = {
      phone: {
         name: outbound.name,
         definition: p.definition as IPhoneDefinition,
      },
      strategy: {
         name: strategy.name,
         definition: s.definition as IStrategyDefinition,
      },
      conversation: {
         name: conversation.name,
         definition: c.definition as IConversationDefinition,
      },
   }
   const playbook = {
      ...playbookFromContent(snapshot, RECOMMENDED_PLAYBOOK),
      /* So the script and message-group pickers show what is in use. */
      scriptId: String(outbound.contentId),
      messageGroupId: String(strategy.contentId),
   }
   return {
      playbook,
      bound: {
         categoryId,
         hashes: hashes(playbook),
         refs: {
            outboundScript: refOf(p),
            strategy: refOf(s),
            conversation: refOf(c),
            inboundScript: i ? refOf(i) : null,
         },
      },
   }
}

/**
 * Make sure the library holds exactly the words in the draft, then return
 * the references to put in the configuration. Only the slots whose words
 * changed are written: a recommended (shared) or earlier item is customized
 * into this organization's own copy; with nothing to start from, it is
 * created. Each reference is stored as soon as it comes back, because content
 * calls take no idempotency key.
 */
const ensureContent = async (
   draft: IFlowDraft,
   type: TLeadType,
   categoryId: number
): Promise<IPlaybookRefs | null> => {
   const pb = viewFor(draft.playbook, type)
   const now = hashes(pb)
   const key = String(categoryId)
   const base = session.boundBy?.[key] ?? session.recommendedBy?.[key] ?? null
   const campaign = draft.audience.name || 'New campaign'
   /* Each lead type's items are named after it, so they are told apart in the library. */
   const name =
      audienceTypes(draft.audience).length > 1
         ? `${campaign} · ${LEAD_TYPE_LABEL[type]}`
         : campaign
   const defs = {
      phone: () => toPhoneDefinition(pb, categoryId, name),
      strategy: () => toStrategyDefinition(pb, categoryId, name),
      conversation: (): IConversationDefinition => ({
         name: `${name} - Reply instructions`,
         businessTypeId: categoryId,
         content: pb.replyInstructions,
      }),
   }
   const slotRef: Record<TContentSlot, keyof IPlaybookRefs> = {
      phone: 'outboundScript',
      strategy: 'strategy',
      conversation: 'conversation',
   }
   const bound: IBoundContent = base
      ? { ...base, hashes: { ...base.hashes }, refs: { ...base.refs } }
      : {
           categoryId,
           hashes: { phone: '', strategy: '', conversation: '' },
           refs: {} as IPlaybookRefs,
        }
   for (const slot of ['phone', 'strategy', 'conversation'] as const) {
      const current = bound.refs[slotRef[slot]] as IContentReference | undefined
      if (current && bound.hashes[slot] === now[slot]) continue
      const rev = current
         ? await api.customizeContent(current, defs[slot]())
         : await api.createContent(slot, defs[slot]())
      ;(bound.refs as unknown as Record<string, IContentReference>)[
         slotRef[slot]
      ] = refOf(rev)
      bound.hashes[slot] = now[slot]
      remember({ boundBy: { ...session.boundBy, [key]: bound } })
   }
   remember({ boundBy: { ...session.boundBy, [key]: bound } })
   return bound.refs
}

/* ---------- Voice ---------- */

/** GET /svc/campaigns/voices/public: every active public voice, with previews and dates. */
const publicVoices = async () =>
   listOf(
      await getData<unknown>('/svc/campaigns/voices/public').catch(() => null)
   )
      .filter((r) => !r.status || r.status === 'active')
      .map((r) => ({
         voiceId: String(r.voice_id ?? r.voiceId ?? ''),
         name: String(r.name ?? 'Voice'),
         description: String(r.description ?? ''),
         gender: String(r.gender ?? ''),
         accent: String(r.accent ?? ''),
         previewUrl: String(r.preview_url ?? r.previewUrl ?? ''),
         createdAt: String(r.created_at ?? r.createdAt ?? ''),
      }))
      .filter((v) => v.voiceId)
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt))

/** The public catalog, or null when it is unavailable (so the older list is used). */
const latestVoices = async () => {
   const list = await publicVoices()
   return list.length ? list : null
}

let voiceCache: IVoiceOption[] | null = null

const ensureAssistant = async (campaignId: string, draft: IFlowDraft) => {
   const { optionId, name } = draft.voice
   if (!optionId || !name.trim()) return
   const a = session.assistant
   if (a && a.voiceId === optionId && a.name === name) return
   if (a) {
      await api.updateAssistant(campaignId, a.id, {
         assistantName: name.trim(),
         voiceId: optionId,
      })
      remember({ assistant: { id: a.id, voiceId: optionId, name } })
      return
   }
   const created = await api.addAssistant(campaignId, {
      assistantName: name.trim(),
      voiceId: optionId,
   })
   remember({ assistant: { id: created.id, voiceId: optionId, name } })
}

/* ---------- Numbers ---------- */

/** Numbers seen in search results, so a pick can be reserved with the row the backend gave. */
const seen = new Map<string, IAvailableNumber>()

const syncNumbers = async (campaignId: string, draft: IFlowDraft) => {
   const wanted = draft.numbers.selected
      .filter((n) => !n.owned)
      .map((n) => toE164(n.number))
   const reserved = session.reserved || []
   const toReserve = wanted.filter((n) => !reserved.includes(n))
   const toDrop = reserved.filter((n) => !wanted.includes(n))
   if (toDrop.length) await api.removeNumbers(campaignId, toDrop)
   const kept = reserved.filter((n) => !toDrop.includes(n))
   for (const n of toReserve) {
      let row = seen.get(n)
      if (!row) {
         const again = await api.availableNumbers(areaOf(n))
         again.forEach((r) => seen.set(toE164(r.phoneNumber), r))
         row = seen.get(n)
      }
      if (!row)
         throw new Error(
            `${toDisplay(n)} was just taken. Pick another number in Phone numbers.`
         )
      await api.reserveNumber(campaignId, row)
      kept.push(n)
      remember({ reserved: [...kept] })
   }
   remember({ reserved: kept })
   return toReserve.length > 0 || toDrop.length > 0
}

/* ---------- Results, timing and voices from the existing analytics ---------- */

/** GET returning the envelope's `data`. */
const getData = async <T>(apiPath: string, params?: Record<string, unknown>) =>
   (
      await apiGet<{ data?: T }>({
         apiPath,
         ...(params ? { config: { params } } : {}),
      })
   )?.data as T

type TStatsResponse = {
   series?: TDayRow[]
   totals?: Record<string, number>
   campaigns?: Array<Record<string, unknown>>
}

/** Org-wide daily outreach (GET /campaigns/stats?from&to), with a per-campaign breakdown. */
const orgStats = (from: string, to: string) =>
   getData<TStatsResponse>('/campaigns/stats', { from, to })

/** One campaign's daily outreach (GET /campaigns/outreach/:id/stats?from&to). */
const campaignSeries = async (id: string, from: string, to: string) =>
   listOf(
      (
         await getData<TStatsResponse>(
            `/campaigns/outreach/${encodeURIComponent(id)}/stats`,
            { from, to }
         )
      )?.series
   ) as TDayRow[]

/** All-time leads and contracts for one campaign (the campaign page's own stats). */
const campaignSnapshot = async (id: string) => {
   try {
      const r = (await CampaignService.getCampaignByLeadSource(
         id,
         'campaign'
      )) as unknown as {
         campaignStats?: { topMetrics?: { current?: Record<string, number> } }
      }
      const c = r?.campaignStats?.topMetrics?.current || {}
      return { leads: num(c.totalLeads), contracts: num(c.totalContracts) }
   } catch {
      return { leads: 0, contracts: 0 }
   }
}

/** The hour calls connect best for a campaign (UTC from the backend), in local time. */
const bestHour = async (campaignId: string) => {
   const rows = await getData<Array<{ hour?: number }>>(
      `/analytics/best-time-to-call/${encodeURIComponent(campaignId)}`,
      { statType: 'campaign' }
   )
   const h = listOf(rows)[0]?.hour
   return typeof h === 'number' ? localHour(h) : null
}

/** For a new campaign: the best hour of the organization's first live campaign. */
let orgBestHour: Promise<string | null> | null = null
const bestHourForOrg = () => {
   if (!orgBestHour)
      orgBestHour = CampaignService.getActiveCampaigns({ page: 1, limit: 25, status: 'active' })
         .then(listOf)
         .then((list) => list.find((c) => c.status === 'active'))
         .then((c) => (c ? bestHour(String(c.id)) : null))
         .catch(() => null)
   return orgBestHour
}

/**
 * Appointment rate per voice: each live campaign reports appointments and
 * leads per assistant (GET /analytics/top-agents/:id); assistants are matched
 * to their voice through the campaign's assistants.
 */
const voicePerformance = async () => {
   const live = (
      await CampaignService.getActiveCampaigns({ page: 1, limit: 25, status: 'active' }).then(
         listOf
      )
   )
      .filter((c) => c.status === 'active')
      .slice(0, 10)
   const totals = new Map<string, { appts: number; leads: number }>()
   await Promise.all(
      live.map(async (c) => {
         const data = (c.data || {}) as Record<string, unknown>
         const voiceOf = new Map(
            listOf(data.aiAssistants).map((a) => [
               String(a.id),
               String(a.voiceId || a.voice_id || ''),
            ])
         )
         const rows = listOf(
            await getData<unknown>(
               `/analytics/top-agents/${encodeURIComponent(String(c.id))}`,
               { page: 1, limit: 50, statType: 'campaign' }
            ).catch(() => [])
         )
         rows.forEach((r) => {
            const voice = voiceOf.get(String(r.assistantId))
            if (!voice) return
            const t = totals.get(voice) || { appts: 0, leads: 0 }
            t.appts += num(r.totalAppointments)
            t.leads += num(r.totalAssigned)
            totals.set(voice, t)
         })
      })
   )
   const out = new Map<string, number>()
   totals.forEach((t, voice) => {
      /* Too few leads to call it a rate. */
      if (t.leads >= 20)
         out.set(voice, Math.round((t.appts / t.leads) * 1000) / 10)
   })
   return out
}

/** The city an area code serves, from the number search. */
const cityCache = new Map<string, Promise<string>>()
const cityOf = (code: string) => {
   if (!cityCache.has(code))
      cityCache.set(
         code,
         api.availableNumbers(code).then((rows) => {
            rows.forEach((r) => seen.set(toE164(r.phoneNumber), r))
            return String(rows[0]?.locality || rows[0]?.region || '')
         })
      )
   return cityCache.get(code) as Promise<string>
}

/** A voice's style as a short two-word label, like the design's "Warm Consultative". */
export const shortStyle = (
   description?: string | null,
   _gender?: string | null,
   accent?: string | null,
   name?: string | null
) => {
   /* Descriptions are sometimes a greeting ("Hi, This is Erin"): skip greetings and the voice's own name. */
   const skip = new RegExp(
      `^(the|and|with|for|voice|that|this|who|has|are|you|your|hi|hello|hey|its|is${name ? `|${name}` : ''})$`,
      'i'
   )
   const words = (description || '')
      .replace(/[^A-Za-z\s-]/g, ' ')
      .split(/\s+/)
      .filter((w) => w.length > 2 && !skip.test(w))
      .slice(0, 2)
   if (words.length)
      return words
         .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
         .join(' ')
   return accent
      ? `${accent.charAt(0).toUpperCase()}${accent.slice(1)} accent`
      : 'Natural voice'
}

/** The next three steps a live campaign takes, in the words the live page uses. */
export const nextUp = (
   cadence: ICadenceStep[],
   best: string | null,
   enrolled: number
) => {
   let calls = 0
   let texts = 0
   return [...cadence]
      .sort((a, b) => a.day - b.day || a.order - b.order)
      .slice(0, 3)
      .map((step) => {
         const time = best ? timeFor(step.action, best) : step.localTime
         const day =
            step.day <= 1
               ? 'Today'
               : step.day === 2
                 ? 'Tomorrow'
                 : `Day ${step.day}`
         let what: string
         if (step.action === 'call') {
            calls += 1
            what =
               calls === 1
                  ? `First calls to ${enrolled.toLocaleString()} leads`
                  : `Call ${calls}, picking up where each lead left off`
         } else {
            texts += 1
            what =
               texts === 1
                  ? 'Text 1 to anyone who did not answer'
                  : `Text ${texts} to anyone still quiet`
         }
         return { when: `${day} · ${clockTime(time)}`, what }
      })
}

/** The dashboard's campaign_type list → the one type the flow shows. */
const kindOf = (types: unknown): TCampaignKind | null => {
   const list = (Array.isArray(types) ? types : types ? [types] : []).map(
      String
   )
   return list.includes('activity')
      ? 'activity'
      : list.includes('revive')
        ? 'revive'
        : list.includes('new-lead')
          ? 'new-lead'
          : null
}

/* ---------- Results, the same way for the organization and one campaign ---------- */

/** A ratio as a percent metric (answered calls, replied texts). */
const percentMetric = (
   series: TDayRow[],
   w: ReturnType<typeof periodWindows>,
   top: (r: TDayRow) => number,
   bottom: (r: TDayRow) => number
): IMetric => {
   const m = ratioMetric(series, w, top, bottom)
   const r1 = (x: number) => Math.round(x * 1000) / 10
   return {
      value: r1(m.value),
      previous: r1(m.previous),
      trend: m.trend.map(r1),
   }
}

const resultsFrom = (series: TDayRow[], period: TPeriod): IResults => {
   const w = periodWindows(period)
   return {
      period,
      leads: windowMetric(series, w, PICK.leads),
      conversations: windowMetric(series, w, PICK.conversations),
      appointments: windowMetric(series, w, PICK.appointments),
      transfers: windowMetric(series, w, PICK.transfers),
      attemptsPerLead: ratioMetric(series, w, PICK.touches, PICK.leads),
      callsAnswered: percentMetric(
         series,
         w,
         (r) => num(r.calls_connected),
         (r) => num(r.calls_total)
      ),
      textsReplied: percentMetric(
         series,
         w,
         (r) => num(r.sms_replies),
         (r) => num(r.sms_sent)
      ),
   }
}

/* ---------- What needs attention ---------- */

type TConversationRow = Record<string, unknown> & {
   leadId?: number | string
   id?: number | string
   firstName?: string
   lastName?: string
   lastConversation?: { date?: string; content?: string | null; type?: string }
   lastFfConversation?: { date?: string; preview?: string | null; direction?: string; channel?: string }
   meta?: { appointment?: { date?: string } }
   handoff?: { lastAt?: string }
   engagement?: { lastLeadReplyAt?: string }
   assignedCampaignId?: string | number
}

const short = (text: unknown = '', n = 90) => {
   const value = typeof text === 'string' ? text : ''
   return value.length > n ? `${value.slice(0, n - 1).trimEnd()}…` : value
}

/**
 * Recent bookings, live transfers and replies for one campaign,
 * from the conversation list (GET /recent-conversations filtered by
 * campaignId), newest first.
 */
const recentActivity = async (
   campaignId?: string
): Promise<IActivityItem[]> => {
   const since = new Date(Date.now() - 30 * 864e5).toISOString()
   const ask = (params: Record<string, unknown>) =>
      getData<unknown>('/recent-conversations', {
         page: 1,
         limit: 8,
         ...(campaignId ? { campaignId } : {}),
         ...params,
      })
         .then((d) => (listOf(d) as TConversationRow[]).filter((r) => !campaignId || String(r.assignedCampaignId) === campaignId))
   const [booked, transferred, replied] = await Promise.all([
      ask({ hasAppointment: 'true', appointmentStatus: 'upcoming,completed', sortBy: 'latest_activity' }),
      ask({ liveTransfer: 'true', handoffStatus: 'success', sortBy: 'latest_activity' }),
      ask({ textConversations: 'true', lastResponseFrom: since, sortBy: 'latest_response' }),
   ])
   const item = (
      kind: TActivityKind,
      r: TConversationRow,
      text: string,
      at?: string
   ): IActivityItem => {
      const leadId = String(r.leadId ?? r.id ?? '')
      const inCampaign = r.assignedCampaignId ?? campaignId
      return {
         id: `${kind}-${leadId}`,
         kind,
         leadId,
         ...(inCampaign ? { campaignId: String(inCampaign) } : {}),
         leadName:
            [r.firstName, r.lastName].filter(Boolean).join(' ') || 'A lead',
         text,
         at: at || r.lastConversation?.date || '',
         ...(!at ? { timeLabel: 'Last activity' } : {}),
      }
   }
   const when = (iso?: string) =>
      iso
         ? new Date(iso).toLocaleString(undefined, {
              weekday: 'short',
              hour: 'numeric',
              minute: '2-digit',
           })
         : ''
   // Legacy conversation rows do not identify the sender of their preview. Read the
   // inbound timeline item rather than displaying our last outbound SMS as a reply.
   const replies: IActivityItem[] = []
   for (let offset = 0; offset < replied.length; offset += 4) {
      const batch = await Promise.all(replied.slice(offset, offset + 4).map(async (r) => {
         const leadId = String(r.leadId ?? r.id ?? '')
         if (!leadId) return null
         const latest = r.lastFfConversation
         if (latest?.direction === 'inbound' && ['sms', 'email'].includes(latest.channel || '') && latest.date) {
            return item('reply', r, short(latest.preview) || 'Replied', latest.date)
         }
         const timeline = listOf(await getData<unknown>(`/conversations-timeline/${encodeURIComponent(leadId)}`, {
            page: 1, limit: 25, hideExternalContent: true,
         }))
         const reply = timeline
            .filter((event) => event.isIncoming === true && ['text', 'email'].includes(String(event.itemType)) && event.externalContent !== true)
            .sort((a, b) => Date.parse(String(b.ts)) - Date.parse(String(a.ts)))[0]
         return reply ? item('reply', r, short(reply.content) || 'Replied', String(reply.ts || '')) : null
      }))
      batch.forEach((reply) => { if (reply) replies.push(reply) })
   }
   return [
      ...booked.map((r) =>
         item(
            'appointment',
            r,
            `Booked an appointment${r.meta?.appointment?.date ? ` for ${when(r.meta.appointment.date)}` : ''}`
         )
      ),
      ...transferred.map((r) =>
         item('transfer', r, 'Sent live to your team', r.handoff?.lastAt)
      ),
      ...replies,
   ]
      .filter((x) => x.leadId && Date.parse(x.at) >= Date.parse(since))
      .sort((x, y) => Date.parse(y.at) - Date.parse(x.at))
      .slice(0, 12)
}

/* ---------- Saving ---------- */

const rememberRead = (read: IConfigurationRead) =>
   remember({ campaignId: read.campaignId, revision: read.revision })

/**
 * Create the draft once, then save the whole configuration against its
 * revision. A 409 means someone else saved in between: the latest revision is
 * read and the user's draft saved on top of it once ("keep mine").
 */
const saveConfiguration = async (draft: IFlowDraft) => {
   const ids = await categoryIds()
   categoryFor(ids, audienceTypes(draft.audience))
   /* Each lead type's words are written in turn: content calls take no idempotency key. */
   const refs: TPlaybookRefsByType = {}
   for (const t of audienceTypes(draft.audience)) {
      const r = await ensureContent(draft, t, ids[t])
      if (r) refs[t] = r
   }
   const base = toConfiguration(draft, ids, refs)
   /* Every step goes out at the hour calls connect best (the heatmap), not a fixed time. */
   const best =
      (session.campaignId
         ? await bestHour(session.campaignId).catch(() => null)
         : null) ?? (await bestHourForOrg())
   const configuration = {
      ...base,
      source:
         base.source.mode === 'dynamic'
            ? {
                 mode: 'dynamic' as const,
                 criteria: [
                    ...kindCriteria(draft.audience.kinds),
                    ...base.source.criteria,
                 ],
              }
            : base.source,
      cadence: best
         ? base.cadence.map((c) => ({
              ...c,
              localTime: timeFor(c.action, best),
           }))
         : base.cadence,
   }
   const name = draft.audience.name || 'New campaign'

   if (!session.campaignId) {
      if (!session.createKey)
         remember({ createKey: idempotencyKey('campaign-flow-draft') })
      const read = await api.createDraft(
         name,
         configuration,
         session.createKey as string
      )
      rememberRead(read)
      remember({ createKey: undefined })
      return read.campaignId
   }
   const id = session.campaignId
   try {
      rememberRead(
         await api.saveConfiguration(
            id,
            session.revision ?? 0,
            configuration,
            name
         )
      )
   } catch (e) {
      if (!(e instanceof CampaignV2Error) || e.status !== 409) throw e
      const fresh = await api.readConfiguration(id)
      rememberRead(
         await api.saveConfiguration(id, fresh.revision, configuration, name)
      )
   }
   return id
}

/* ---------- The service ---------- */

export const campaignFlowLive: ICampaignFlowService = {
   context: async () => {
      const [user, price] = await Promise.all([
         UserService.getUserData().catch(() => null),
         api.pricing(1).catch(() => null),
      ])
      const org = orgId()
      const membership = (user?.userOrganizations || []).find(
         (o) => o.organizationId === org
      )
      const first = (session.reserved || [])[0]
      return {
         org: {
            organization: membership?.organization?.name || ORG.organization,
            agentName: user?.firstName || ORG.agentName,
            ownerFirstName: user?.firstName || ORG.ownerFirstName,
            phoneNumber: first ? toDisplay(first) : ORG.phoneNumber,
         },
         lead: { ...SAMPLE_LEAD },
         numberPrice:
            typeof price?.currentPeriod?.amount === 'number'
               ? price.currentPeriod.amount
               : NUMBER_PRICE_PER_MONTH,
      }
   },

   /* Entry */

   campaigns: async (period = '30d') => {
      const w = periodWindows(period)
      const [list, org] = await Promise.all([
         allCampaignRows(),
         orgStats(w.currentFrom, w.to).catch(() => null),
      ])
      const byId = new Map(
         listOf(org?.campaigns).map((c) => [String(c.campaignId), c])
      )
      const result: ICampaignRow[] = []
      // Keep per-campaign analytics requests bounded, including accounts with many drafts.
      for (let offset = 0; offset < list.length; offset += 6) {
         const batch = await Promise.all(list.slice(offset, offset + 6).map(async (c): Promise<ICampaignRow> => {
            const [series, snap] = await Promise.all([
               campaignSeries(String(c.id), w.currentFrom, w.to).catch(() => []),
               campaignSnapshot(String(c.id)),
            ])
            const data = (c.data || {}) as Record<string, unknown>
            const assistant = listOf(data.aiAssistants)[0] || {}
            const voiceId = String(assistant.voiceId || assistant.voice_id || '')
            const range = byId.get(String(c.id)) || {}
            const status = String(c.status || '')
            return {
               id: String(c.id),
               name: String(c.campaignName || c.source || 'Campaign'),
               kind: kindOf(c.campaignType ?? data.campaign_type),
               // The compact list does not include audience or voice configuration.
               // Do not imply "All sources" when that information was not returned.
               source: String(c.source || ''),
               status: status === 'active' ? 'live' : status === 'paused' ? 'paused' : 'draft',
               voiceHue: voiceId ? hueOf(voiceId) : 262,
               voiceName: String(assistant.assistantName || ''),
               leads: num(range.leads_assigned) || snap.leads,
               reached: num(range.leads_contacted),
               appointments: num(range.appointments_ff),
               transfers: num(range.handoffs_success),
               trend: buckets(series, w.currentFrom, w.days, PICK.appointments),
               lastActivity: lastActive(series) ?? (status === 'active' ? ago(c.lastUpdated) : 'Not started'),
            }
         }))
         result.push(...batch)
      }
      return result
   },

   overview: async (period) => {
      const w = periodWindows(period)
      const [org, rows] = await Promise.all([
         orgStats(w.from, w.to),
         CampaignService.getActiveCampaigns({ page: 1, limit: 100, status: 'active' })
            .then(listOf)
            .catch(() => []),
      ])
      return {
         ...resultsFrom(listOf(org?.series) as TDayRow[], period),
         liveCampaigns: rows.filter((c) => c.status === 'active').length,
      } satisfies IOverviewStats
   },

   starters: async () => [],

   /* Who we call */

   /* The keyword reader runs on the organization's own sources, tags, stages and ponds. */
   interpretDescription: async (text) => interpretDescription(text),

   /*
    * Kinds, lead types and filter rows together. "New or recently active" is
    * not one query on this endpoint, so it is counted in parts that do not
    * overlap and added up (utils/live-derive.ts).
    */
   estimateLeads: async (audience: IAudience) => {
      const queries = countQueries(audience)
      const counts = await Promise.all(
         queries.map((criteria) =>
            apiPost<{ data?: { count?: number | string } }>({
               apiPath: '/active-lead-sources/estimated-leads',
               data: criteria,
            }).then((r) => num(r?.data?.count))
         )
      )
      return counts.reduce((a, b) => a + b, 0)
   },

   filterVocabulary: async (): Promise<TFilterVocabulary> => {
      const org = orgId()
      const safe = <T>(p: Promise<T>) => p.catch(() => [] as unknown as T)
      const [sources, stages, tags, ponds] = await Promise.all([
         safe(FubService.getPeopleSources({ orgId: org, page: 1, limit: 500 })),
         safe(
            OnboardingV2Service.getFubStages({
               organizationId: org,
               limit: 500,
               page: 1,
            })
         ),
         safe(
            OnboardingV2Service.getPeopleTags({
               orgId: org,
               page: 1,
               limit: 1000,
            })
         ),
         safe(FubService.getPonds({ orgId: org, page: 1, limit: 500 })),
      ])
      const opts = (
         rows: Record<string, unknown>[],
         value: (r: Record<string, unknown>) => unknown,
         label: (r: Record<string, unknown>) => unknown = value
      ) =>
         rows
            .map((r) => ({
               value: String(value(r) ?? ''),
               label: String(label(r) ?? ''),
            }))
            .filter((o) => o.value && o.label)
      return {
         source: opts(listOf(sources), (r) => r.leadSource),
         stage: opts(listOf(stages), (r) => r.stageName),
         tags: opts(listOf(tags), (r) => r.tagName),
         assigned_pond_id: opts(
            listOf(ponds),
            (r) => r.pondId ?? r.id,
            (r) => r.pondName ?? r.name ?? r.pondId ?? r.id
         ),
         inquired_property: [],
      }
   },

   /* Playbook */

   recommendedPlaybook: async (audience) => {
      const clone = <T>(v: T) => JSON.parse(JSON.stringify(v)) as T
      const types: TLeadType[] = audienceTypes(audience)
      /* One lead type's recommended words, or the built-in ones when the library has none. */
      const forType = async (t: TLeadType) => {
         try {
            const ids = await categoryIds()
            const categoryId = categoryFor(ids, [t])
            let found = recommendedCache.get(categoryId)
            if (!found) {
               const loaded = await loadRecommended(categoryId)
               if (!loaded) return null
               found = loaded
               recommendedCache.set(categoryId, found)
            }
            remember({
               recommendedBy: {
                  ...session.recommendedBy,
                  [String(categoryId)]: found.bound,
               },
            })
            return clone(found.playbook)
         } catch {
            /* Library unavailable: start from the built-in words; they are written to the library on save. */
            return null
         }
      }
      const loaded = await Promise.all(types.map(forType))
      const first = loaded[0] ?? clone(RECOMMENDED_PLAYBOOK)
      return withTypes(
         {
            ...first,
            byType: Object.fromEntries(
               types.map((t, i) => [
                  t,
                  loaded[i]
                     ? pickContent(loaded[i] as IPlaybook)
                     : clone(RECOMMENDED_BY_TYPE[t]),
               ])
            ),
         },
         types
      )
   },

   /*
    * The library for one lead type: Forward Flow's shared items and this
    * organization's own, from the catalog (`shared` tells them apart).
    */
   contentOptions: async (type, slot) => {
      const categoryId = categoryFor(await categoryIds(), [type])
      const items = await api.catalog(
         slot === 'script' ? 'phone' : 'strategy',
         categoryId
      )
      return (slot === 'script' ? items.filter(isOutboundAi) : items).map(
         (i): IContentOption => ({
            id: String(i.contentId),
            name: i.name,
            owner: i.shared ? 'recommended' : 'yours',
            summary:
               slot === 'messages' && i.counts
                  ? `${plural(i.counts.sms, 'text')} · ${plural(i.counts.voicemail, 'voicemail')}`
                  : undefined,
         })
      )
   },

   /*
    * Pick a library item: its words come into the playbook and its revision is
    * kept as the reference, so saving an unchanged pick writes nothing new.
    */
   loadContent: async (type, slot, id) => {
      const categoryId = categoryFor(await categoryIds(), [type])
      const kind = slot === 'script' ? 'phone' : 'strategy'
      const [items, rev] = await Promise.all([
         api.catalog(kind, categoryId).catch(() => [] as ICatalogItem[]),
         api.acquireRevision(kind, Number(id)),
      ])
      const name = items.find((i) => String(i.contentId) === id)?.name
      const key = String(categoryId)
      const base: IBoundContent = session.boundBy?.[key] ??
         session.recommendedBy?.[key] ?? {
            categoryId,
            hashes: { phone: '', strategy: '', conversation: '' },
            refs: {} as IPlaybookRefs,
         }
      const bound: IBoundContent = {
         ...base,
         hashes: { ...base.hashes },
         refs: { ...base.refs },
      }
      let out: Partial<TPlaybookContent>
      if (slot === 'script') {
         const script = ((rev.definition as IPhoneDefinition).sections || [])
            .filter((x) => x.type === 'call_outbound')
            .sort((x, y) => x.order - y.order)
            .map((x, i) => ({
               id: String(x.sectionId ?? `s${i + 1}`),
               title: x.name,
               text: fromTemplate(x.content),
            }))
         bound.refs.outboundScript = refOf(rev)
         bound.hashes.phone = JSON.stringify(
            script.map((x) => [x.title, x.text])
         )
         out = { script, scriptId: id, ...(name ? { scriptName: name } : {}) }
      } else {
         const d = rev.definition as IStrategyDefinition
         const texts = (d.sms || []).map((m, i) => ({
            id: `s${i + 1}`,
            text: fromTemplate(m.template),
         }))
         const voicemails = (d.voicemail || []).map((m, i) => ({
            id: `v${i + 1}`,
            text: fromTemplate(m.template),
         }))
         bound.refs.strategy = refOf(rev)
         bound.hashes.strategy = JSON.stringify([
            texts.map((t) => t.text),
            voicemails.map((v) => v.text),
         ])
         out = {
            texts,
            voicemails,
            messageGroupId: id,
            ...(name ? { messageGroupName: name } : {}),
         }
      }
      remember({ boundBy: { ...session.boundBy, [key]: bound } })
      return out
   },

   /* Test phone: no customer-safe test endpoint yet (gap G4); the drawer simulates in the browser. */
   sendTestText: async () => {
      throw new Error('Test texts to your phone are not available yet.')
   },
   placeTestCall: async () => {
      throw new Error('Test calls to your phone are not available yet.')
   },

   /* Voice */

   /*
    * The current public voice catalog (the one onboarding uses), newest first,
    * with its recorded previews. The campaign-assistant list is the fallback:
    * it drops previews and dates.
    */
   voices: async () => {
      const [catalog, perf] = await Promise.all([
         latestVoices().catch(() => null),
         voicePerformance().catch(() => new Map<string, number>()),
      ])
      const list =
         catalog ??
         (await api.voices()).map((v) => ({
            voiceId: v.voiceId,
            name: v.name,
            description: v.description ?? '',
            gender: v.gender ?? '',
            accent: v.accent ?? '',
            previewUrl: v.previewUrl ?? '',
            createdAt: '',
         }))
      const newest = [...list].sort((a, b) =>
         b.createdAt.localeCompare(a.createdAt)
      )[0]
      const options = list.map((v): IVoiceOption => ({
         id: v.voiceId,
         defaultName: v.name,
         style: shortStyle(v.description, v.gender, v.accent, v.name),
         hue: hueOf(v.voiceId),
         appointmentRate: perf.get(v.voiceId) ?? 0,
         previewUrl: v.previewUrl || null,
         ...(newest?.createdAt && v === newest ? { isNew: true } : {}),
      }))
      /* Best performing first, marked, as the design shows. */
      const best = [...options].sort(
         (a, b) => b.appointmentRate - a.appointmentRate
      )[0]
      if (best && best.appointmentRate > 0) best.bestPerforming = true
      voiceCache = [
         ...options.filter((o) => o.bestPerforming),
         ...options.filter((o) => !o.bestPerforming),
      ]
      return voiceCache
   },

   previewVoice: async (voiceId) => {
      const list = voiceCache || (await campaignFlowLive.voices())
      return list.find((v) => v.id === voiceId)?.previewUrl ?? null
   },

   /* Numbers */

   /*
    * Where the leads live. A campaign with leads uses its own overview; a new
    * draft has none yet, so the shares across the organization's live
    * campaigns stand in. Each code gets its city from the number search.
    */
   areaCodes: async () => {
      const shares = new Map<string, number>()
      const add = (list: Array<{ areaCode: string; percentage: number }>) =>
         list.forEach((t) => {
            const code = String(t.areaCode)
            /* US area codes never start with 0 or 1 ("171" is a +1 number read wrong). */
            if (!/^[2-9]\d{2}$/.test(code)) return
            shares.set(code, (shares.get(code) || 0) + num(t.percentage))
         })
      if (session.campaignId) {
         const o = await CampaignService.getCampaignOverview(
            session.campaignId
         ).catch(() => null)
         add(o?.topAreaCodes || [])
      }
      if (!shares.size) {
         const live = (
            await CampaignService.getActiveCampaigns({ page: 1, limit: 25, status: 'active' })
               .then(listOf)
               .catch(() => [])
         ).filter((c) => c.status === 'active')
         const overviews = await Promise.all(
            live
               .slice(0, 10)
               .map((c) =>
                  CampaignService.getCampaignOverview(String(c.id)).catch(
                     () => null
                  )
               )
         )
         overviews.forEach((o) => add(o?.topAreaCodes || []))
      }
      if (!shares.size) {
         const owned = await api.inventoryNumbers().catch(() => [])
         owned.forEach((n) =>
            add([{ areaCode: areaOf(n.phoneNumber), percentage: 1 }])
         )
      }
      const total = [...shares.values()].reduce((a, b) => a + b, 0) || 1
      const top = [...shares.entries()].sort((a, b) => b[1] - a[1]).slice(0, 5)
      const cities = await Promise.all(
         top.map(([code]) => cityOf(code).catch(() => ''))
      )
      return top.map(([code, share], i) => ({
         code,
         city: cities[i],
         leadShare: Math.round((share / total) * 100),
      }))
   },

   /* Area code ("719"), city ("Colorado Springs") or state ("CO"). */
   availableNumbers: async (search) => {
      const q = search.trim()
      if (!q) return []
      const digits = q.replace(/\D/g, '')
      /* "Austin, TX" is not understood by the search; the city alone is. */
      const term =
         digits.length >= 3 ? digits.slice(0, 3) : q.split(',')[0].trim()
      const rows = await api.availableNumbers(term)
      return rows
         .filter((r) => r.phoneNumber)
         .map((r): IPhoneNumberOption => {
            const e164 = toE164(r.phoneNumber)
            seen.set(e164, r)
            return {
               number: toDisplay(e164),
               areaCode: areaOf(e164),
               city: String(r.locality || r.region || ''),
            }
         })
   },

   ownedNumbers: async () =>
      (await api.inventoryNumbers())
         .filter((n) => !n.isUsedInCampaigns)
         .map((n) => ({
            number: toDisplay(n.phoneNumber),
            areaCode: areaOf(n.phoneNumber),
            city: n.locality || n.region || '',
            owned: true,
         })),

   /* Who gets the leads */

   teammates: async () =>
      (await api.agents()).map((a) => ({
         id: a.crmUserId,
         name: a.name,
         initials:
            a.name
               .split(/\s+/)
               .map((w) => w[0])
               .join('')
               .slice(0, 2)
               .toUpperCase() || '?',
      })),

   /* Lifecycle */

   saveDraft: async (draft) => {
      if (draft.id && session.campaignId && draft.id !== session.campaignId)
         startNewLiveSession()
      if (draft.id && !session.campaignId) {
         const read = await api.readConfiguration(draft.id)
         rememberRead(read)
      }
      const id = await saveConfiguration(draft)
      await ensureAssistant(id, draft)
      /* Reserving changes the configuration on the server, so read the new revision. */
      if (await syncNumbers(id, draft))
         rememberRead(await api.readConfiguration(id))
      return { id }
   },

   review: async (draft) => {
      const items = localReadiness(draft)
      let ready = items.every((i) => i.ok)
      let toBuy = draft.numbers.selected.filter((n) => !n.owned).length
      if (session.campaignId) {
         const p = await api.preview(session.campaignId)
         ready = ready && p.ready
         toBuy = p.numbersToPurchase
         p.reasons.forEach((r) => items.push(reasonToItem(r)))
      }
      const price = toBuy ? await api.pricing(toBuy).catch(() => null) : null
      const amount = price?.currentPeriod?.amount
      return {
         ready,
         items,
         costs: [
            {
               label: `${toBuy} new number${toBuy === 1 ? '' : 's'}`,
               amount:
                  typeof amount === 'number'
                     ? `$${amount.toFixed(2)}/month`
                     : toBuy
                       ? 'Charged when you launch'
                       : '$0',
            },
            {
               label: 'Calls, texts and voicemails',
               amount: 'Included in your plan',
            },
         ],
      }
   },

   activate: async () => {
      const id = session.campaignId
      if (!id) throw new Error('Save the campaign before launching it.')
      const p = await api.preview(id)
      if (!p.ready)
         throw new Error(
            p.reasons.map((r) => reasonToItem(r).label).join(' ') ||
               'The campaign is not ready to launch yet.'
         )
      /* The same key on every retry, so a timed-out launch never runs twice. */
      if (!session.activateKey)
         remember({ activateKey: idempotencyKey(`activate-${id}`) })
      await api.activate(id, session.activateKey as string)
      remember({ activateKey: undefined })
   },

   setPaused: async (id, paused) => {
      if (paused) return api.setStatus(id, 'paused')
      await api.activate(id, idempotencyKey(`resume-${id}`))
   },

   /* One campaign's page: the same results as the organization's, plus what it is set up to do. */
   campaignStats: async (id, period) => {
      const w = periodWindows(period)
      const [series, snap, cfg, campaign, ids] = await Promise.all([
         campaignSeries(id, w.from, w.to).catch(() => [] as TDayRow[]),
         campaignSnapshot(id),
         api.readConfiguration(id).catch(() => null),
         api.readCampaign(id).catch(() => null),
         categoryIds().catch(() => null),
      ])
      const c = cfg?.configuration
      const status = String(campaign?.status || cfg?.status || '')
      const typeOf = (categoryId: number) =>
         (Object.keys(ids || {}) as TLeadType[]).find(
            (t) => ids?.[t] === categoryId
         )
      const types = (c?.businessTypeIds || [])
         .map(typeOf)
         .filter((t): t is TLeadType => !!t)
      /* The saved campaign's type: created_at >= line is new, < line is cold. */
      const age =
         c?.source.mode === 'dynamic'
            ? c.source.criteria.find((x) => x.field === 'created_at')
            : null
      const isNew = !!age && ['>=', '>'].includes(age.operator)
      const kind = !age ? 'All leads' : isNew ? 'New leads' : 'Cold leads'
      const filters =
         c?.source.mode === 'dynamic'
            ? c.source.criteria.filter((x) => x.field !== 'created_at').length
            : 0
      const who =
         types.length && types.length < 3
            ? types.map((t) => LEAD_TYPE_LABEL[t]).join(' and ')
            : 'Every lead type'
      const cadence = (c?.cadence || []).filter((x) => x.action !== 'email')
      const calls = cadence.filter((x) => x.action === 'call').length
      const days = cadence.length ? Math.max(...cadence.map((x) => x.day)) : 0
      return {
         ...resultsFrom(series, period),
         name: campaign?.campaignName || cfg?.name || 'Campaign',
         kind: !age ? null : isNew ? 'new-lead' : 'revive',
         voiceName: campaign?.aiAssistants?.[0]?.assistantName || undefined,
         voiceHue: campaign?.aiAssistants?.[0]?.voiceId
            ? hueOf(String(campaign.aiAssistants[0].voiceId))
            : undefined,
         status:
            status === 'active'
               ? 'live'
               : status === 'paused'
                 ? 'paused'
                 : 'draft',
         enrolled: snap.leads,
         setup: {
            audience: `${kind} · ${who}${filters ? ` · ${plural(filters, 'filter')}` : ''}`,
            timing: cadence.length
               ? `${plural(cadence.length, 'touch', 'touches')} over ${plural(days, 'day')} · ${plural(calls, 'call')}, ${plural(cadence.length - calls, 'text')}`
               : 'No timing yet',
            numbers: (campaign?.phoneNumbers || c?.phoneNumbers || []).map(
               toDisplay
            ),
            handoff:
               c?.routing?.strategy === 'jump_ball'
                  ? 'Team, first to pick up'
                  : c?.routing?.agentIds?.length
                    ? 'Team, in turns'
                    : 'Nobody yet',
         },
      } satisfies ICampaignStats
   },

   recentActivity: (campaignId) => recentActivity(campaignId),
}
